import { requestMonarch } from '@red-digital/bricks';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import { NextRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Store } from 'redux';
import { ThemeProvider } from 'styled-components';
import { DEFAULT_FUSE_NUMBER } from '~/constants/config/cohesion';
import { DEFAULT_API_VERSION, MONARCH_RULESETS } from '~/constants/config/common';
import { addressFormatAllconnect } from '~/helpers/addressFormatter';
import clientSide from '~/helpers/clientSide';
import { trackEvent } from '~/helpers/tracking';
import { setAddress } from '~/redux/actions/address';
import { setMonarchDecision, setPageLoading } from '~/redux/actions/common';
import { setNotifications } from '~/redux/actions/global';
import { EventData } from '~/types/events';
import { GlobalStyles, theme } from '../styles';
import { useStoreVariablesValidation } from '~/hooks/useSessionValidation';
import { MonarchDecisionPayload } from '~/types/common';

const Debugger = dynamic(() => import('../components/Debugger'), { ssr: false });

export interface FuelProviderCallbackProps {
  isOutageHappening: boolean;
}
interface FuelContextProps {
  router: NextRouter;
  production: boolean;
  trackEvent: ({ action, data, event }: EventData) => void;
  fuseNumber: string;
}

interface FuelProviderProps {
  children: JSX.Element[] | JSX.Element;
  store: Store;
  production: boolean;
  router: NextRouter;
  callback: (props: FuelProviderCallbackProps) => void;
}

export const FuelContext = React.createContext<FuelContextProps | null>(null);

const FuelProvider: FC<FuelProviderProps> = ({ children, store, router, production, callback }) => {
  const [fuseNumber, setFuseNumber] = useState(null);

  // Remove the loader when the route changes.
  if (router?.events?.on) {
    router.events.on('routeChangeComplete', (url, options) => {
      const shallow = options?.shallow ?? false;

      if (url.includes('/confirmation')) {
        trackEvent({
          action: 'elementViewed',
          data: {
            elementType: 'PAGE TRANSITION',
            location: '/confirmation',
            position: 'TRANSITION START',
            text: 'tracking?.redirectUrl',
          },
        });
      }
      if (!shallow && !url.includes('/confirmation')) {
        store.dispatch(setNotifications());
        store.dispatch(setPageLoading(false));
      }
    });
  }

  const monarchCallback = (err: unknown, monarchResponse: unknown) => {
    if (typeof monarchResponse === 'object') {
      const rules = Object.entries(monarchResponse || {});
      const valueMap = { apiVersion: DEFAULT_API_VERSION } as MonarchDecisionPayload;

      if (rules.length) {
        rules.forEach(([key, returnValue]) => (valueMap[key] = returnValue));
        const isOutageHappening = valueMap.outageForm === true || valueMap.outageForm === 'true';
        callback({ isOutageHappening });
        store.dispatch(setMonarchDecision(valueMap));
      }
    }
  };

  useEffect(() => {
    window?.cohesion('fuse:leasesReceived', (leases) => {
      setFuseNumber(leases?.[0]?.dnis);
    });

    window?.cohesion('fuse:error', () => setFuseNumber(DEFAULT_FUSE_NUMBER));
  }, []);

  useEffect(() => {
    if (clientSide) {
      const olVersionMlp = window?.sessionStorage?.getItem('olVersion');
      const apiVersionCookie = Cookies.get('apiVersion');
      const olVersion = apiVersionCookie ? apiVersionCookie : olVersionMlp;

      const payload = {
        ol_version: olVersion,
      };

      // Wait for cohesion to be fully ready (which happens after automatic pageview events)
      window?.cohesion('ready', () => {
        // Ensure monarch is also ready before making the request
        window?.cohesion('monarch:ready', () => {
          requestMonarch({ method: 'ruleset', id: MONARCH_RULESETS.GLOBAL, payload, callback: monarchCallback });
        });
      });
    }
  }, [router.query]);

  useEffect(() => {
    if (clientSide) {
      const addressCookie = Cookies.get('address');
      if (addressCookie) {
        const parsedAddress = JSON.parse(addressCookie);
        const formattedAddress = addressFormatAllconnect(parsedAddress);
        store.dispatch(setAddress('install', formattedAddress));
      }
    }
  }, [clientSide]);

  // Use the session validation hook
  const address = store.getState()?.address?.install?.address;
  const selectedProductId = store.getState()?.products?.selected?.internet?.productId;

  useStoreVariablesValidation(address, selectedProductId, router);

  return (
    <ReduxProvider store={store}>
      <FuelContext.Provider
        value={{
          production,
          router,
          trackEvent,
          fuseNumber,
        }}
      >
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          {children}
          {!production && <Debugger />}
        </ThemeProvider>
      </FuelContext.Provider>
    </ReduxProvider>
  );
};

FuelProvider.displayName = 'FuelProvider';

export default FuelProvider;
